import React, { useEffect } from 'react';
import Typography from '@material-ui/core/Typography';
import { Box, ListItem, List } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import WarningIcon from '@material-ui/icons/Warning';
import moment from 'moment';
import { convertMilitaryStringToAmPm } from '../../helper';

const useStyles = makeStyles(() => ({
  flexContainer: {
    gap: '20px',
    justifyContent: 'space-between',
  },
  listLabel: {
    fontWeight: '500',
    color: '#637381',
    width: '30%'
  },
  loader: {
    float: 'right',
    right: '10px',
    color: 'rgb(64, 169, 255)',
    fontSize: '13px',
  },
  loaderIcon: {
    float: 'right',
    right: '10px',
    fontSize: '20px',
    cursor: 'pointer',
  },
  listWidth: {
    maxWidth: 'min-content',
  },
  scroll: {
    height: '11rem',
    overflowY: 'scroll',
  },
  warning: {
    color: '#ffcc00',
    fontSize: '16px',
    position: 'relative',
    top: '.2rem',
  },
  pr: {
    width: '12rem',
    paddingRight: '0 !important',
  },
  scroll_sm: {
    maxHeight: '11rem',
    overflowY: 'scroll',
  },
  mainBox:{
     marginBottom: 24,
     fontWeight:700
  },
  listRoot: {
    padding: 0,
    color: '#212B36',
    width: '100%',
  },
  listItem: {
    marginBottom: 12,
    padding: 0
  },
  listLabel2:{
    textAlign:'right',
    width: '70%'
  }
}));

const UnifiedHealthCheckDetails = ({
  healthCheckSummary,
  updating,
  fetchRecord,
}) => {
  const classes = useStyles();
  const {
    status,
    start_time,
    end_time,
    completed_inspections_count,
    total_inspections_count,
    poor_or_danger_condition_count,
    pending_upsells_list,
    healthCheckUrl,
    date,
    hcIsComplete,
  } = healthCheckSummary;
  const today = moment().format('YYYY-MM-DD');

  useEffect(() => {
    // Fetch updated record every 30 seconds to provide updated health check summary
    let interval;
    if (date === today) {
      interval = setInterval(() => {
        if (!updating) {
          fetchRecord();
        }
      }, 30000);
    }
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Typography variant="h5" className={classes.mainBox}>
        Service Report
        {updating ? (
          <span className={classes.loader}>Updating...</span>
        ) : (
          <svg onClick={fetchRecord} className={classes.loaderIcon} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M16.6663 9.16658C16.4625 7.7001 15.7822 6.3413 14.7302 5.2995C13.6782 4.25769 12.3128 3.59068 10.8444 3.40121C9.376 3.21174 7.88603 3.51032 6.60401 4.25096C5.32199 4.99159 4.31905 6.1332 3.74967 7.49992M3.33301 4.16658V7.49992H6.66634M3.33301 10.8333C3.53681 12.2998 4.21712 13.6586 5.26914 14.7004C6.32117 15.7422 7.68654 16.4092 9.15495 16.5987C10.6234 16.7881 12.1133 16.4896 13.3953 15.7489C14.6774 15.0083 15.6803 13.8667 16.2497 12.5M16.6663 15.8333V12.5H13.333" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        )}
      </Typography>
      <Box>
        <List className={classes.listRoot}>
          <ListItem className={classes.listItem}>
            <span className={classes.listLabel}>Status </span>
            <span className={classes.listLabel2}>{status}</span>
          </ListItem>
          {start_time && (
          <ListItem className={classes.listItem}>
            <span className={classes.listLabel}>Start Time</span>
            <span className={classes.listLabel2}>{convertMilitaryStringToAmPm(start_time)}</span>
          </ListItem>
          )}
          {end_time && (
          <ListItem className={classes.listItem}>
            <span className={classes.listLabel}>End Time</span>
            <span className={classes.listLabel2}>{convertMilitaryStringToAmPm(end_time)}</span>
          </ListItem>
          )}
          <ListItem className={classes.listItem}>
            <span className={classes.listLabel}>Checks Complete </span>
            <span className={classes.listLabel2}>{completed_inspections_count}{'/'}{total_inspections_count}</span>
          </ListItem>

        </List>
      </Box>
      <Typography variant="body2">
        <Box
          display={{ xs: 'block', md: 'flex' }}
          className={classes.flexContainer}
        >
        <Box>
            {poor_or_danger_condition_count > 0 && (
              <Box mt={1.2}>
                <span>
                  <span role="img" aria-label="warning">
                    <WarningIcon className={classes.warning} />
                  </span>{' '}
                  <b>{poor_or_danger_condition_count}</b>{' '}
                  {poor_or_danger_condition_count > 1
                    ? 'checks have a '
                    : 'check has a '}
                  Poor/Danger Status
                </span>
              </Box>
            )}
            {healthCheckUrl && hcIsComplete && (
              <Box mt={1.2}>
                <a href={healthCheckUrl} target="_blank" rel="noreferrer">
                  Click for Full Details
                </a>
              </Box>
            )}
          </Box>
          {pending_upsells_list.length > 0 && (
            <>
              <Box
                className={classes.listWidth}
                display={{ xs: 'none', xl: 'block' }}
              >
                <Box mt={1} className={classes.pr}>
                  <span>
                    <b>{pending_upsells_list.length}</b> recommended{' '}
                    {pending_upsells_list.length === 1 ? 'service' : 'services'}
                  </span>
                </Box>
                <Box className={`${classes.scroll} scroll_bar`}>
                  {pending_upsells_list.map((list) => (
                    <Box mt={1} key={list}>
                      {list}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box display={{ xs: 'block', xl: 'none' }}>
                <Box mt={1} className={classes.pr}>
                  <span>
                    <b>{pending_upsells_list.length}</b> recommended{' '}
                    {pending_upsells_list.length === 1 ? 'service' : 'services'}
                  </span>
                </Box>
                <Box>
                  {pending_upsells_list.slice(0, 4).map((list) => (
                    <Box mt={1} key={list}>
                      {list}
                    </Box>
                  ))}
                </Box>
              </Box>
              {pending_upsells_list[4] && (
                <Box mt={{ md: 3.5 }} display={{ xs: 'block', xl: 'none' }}>
                  <Box>
                    {pending_upsells_list.slice(4, 8).map((list) => (
                      <Box mt={1} key={list}>
                        {list}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              {pending_upsells_list[8] && (
                <Box
                  className={`${classes.scroll_sm} scroll_bar`}
                  mt={{ md: 3.5 }}
                  display={{ xs: 'block', xl: 'none' }}
                >
                  <Box>
                    {pending_upsells_list.slice(8).map((list) => (
                      <Box mt={1} key={list}>
                        {list}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </Typography>
    </>
  );
};

export default UnifiedHealthCheckDetails;
